import React, { useEffect, useState } from "react";

const API_URL =
  "https://etoile-bricolage-api.imanehibouzid2001.workers.dev";

function AddProduct() {
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // =====================================================
  // CHARGER LES CATEGORIES DEPUIS D1
  // =====================================================

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        setError("");

        const response = await fetch(
          `${API_URL}/api/categories`
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.error ||
              "Impossible de charger les catégories."
          );
        }

        setCategories(data.categories || []);
      } catch (err) {
        console.error(err);

        setError(
          err.message ||
            "Erreur lors du chargement des catégories."
        );
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // =====================================================
  // MODIFICATION DES CHAMPS
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setMessage("");
    setError("");
  };

  // =====================================================
  // SELECTION DE LA PHOTO
  // =====================================================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    setMessage("");
    setError("");

    if (!file) {
      setImage(null);
      setPreview("");
      return;
    }

    if (!file.type.startsWith("image/")) {
      setImage(null);
      setPreview("");

      setError(
        "Veuillez sélectionner une image valide."
      );

      return;
    }

    // Maximum 5 Mo
    if (file.size > 5 * 1024 * 1024) {
      setImage(null);
      setPreview("");

      setError(
        "La photo ne doit pas dépasser 5 Mo."
      );

      return;
    }

    setImage(file);

    const previewUrl =
      URL.createObjectURL(file);

    setPreview(previewUrl);
  };

  // =====================================================
  // AJOUT DU PRODUIT
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    // Catégorie obligatoire
    if (!formData.category_id) {
      setError(
        "Veuillez sélectionner une catégorie."
      );
      return;
    }

    // Photo obligatoire
    if (!image) {
      setError(
        "Veuillez sélectionner une photo."
      );
      return;
    }

    try {
      setLoading(true);

      const dataToSend = new FormData();

      // Nom facultatif
      dataToSend.append(
        "name",
        formData.name.trim()
      );

      // Description facultative
      dataToSend.append(
        "description",
        formData.description.trim()
      );

      // Catégorie obligatoire
      dataToSend.append(
        "category_id",
        formData.category_id
      );

      // Photo obligatoire
      dataToSend.append(
        "image",
        image
      );

     const response = await fetch(
  `${API_URL}/api/products`,
  {
    method: "POST",
    credentials: "include",
    body: dataToSend,
  }
);

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            "Impossible d'ajouter le produit."
        );
      }

      setMessage(
        "Produit ajouté avec succès !"
      );

      // Réinitialisation
      setFormData({
        name: "",
        description: "",
        category_id: "",
      });

      setImage(null);

      if (preview) {
        URL.revokeObjectURL(preview);
      }

      setPreview("");

      // Réinitialiser input file
      const fileInput =
        document.getElementById(
          "product-image"
        );

      if (fileInput) {
        fileInput.value = "";
      }
    } catch (err) {
      console.error(err);

      setError(
        err.message ||
          "Une erreur est survenue."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // AFFICHAGE
  // =====================================================

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <p style={styles.badge}>
            ÉTOILE BRICOLAGE
          </p>

          <h1 style={styles.title}>
            Ajouter un produit
          </h1>

          <p style={styles.subtitle}>
            Ajoutez un nouveau produit à votre catalogue.
          </p>
        </div>

        {message && (
          <div style={styles.success}>
            ✓ {message}
          </div>
        )}

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* CATEGORIE */}

          <div style={styles.group}>
            <label style={styles.label}>
              Catégorie
              <span style={styles.required}>
                {" "}*
              </span>
            </label>

            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              disabled={
                loadingCategories || loading
              }
              style={styles.input}
              required
            >
              <option value="">
                {loadingCategories
                  ? "Chargement..."
                  : "Sélectionner une catégorie"}
              </option>

              {categories.map(
                (category) => (
                  <option
                    key={category.id}
                    value={category.id}
                  >
                    {category.name}
                  </option>
                )
              )}
            </select>
          </div>

          {/* NOM */}

          <div style={styles.group}>
            <label style={styles.label}>
              Nom du produit
              <span style={styles.optional}>
                {" "}— facultatif
              </span>
            </label>

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex. Marteau"
              disabled={loading}
              style={styles.input}
            />
          </div>

          {/* DESCRIPTION */}

          <div style={styles.group}>
            <label style={styles.label}>
              Description
              <span style={styles.optional}>
                {" "}— facultative
              </span>
            </label>

            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description du produit..."
              disabled={loading}
              rows="5"
              style={{
                ...styles.input,
                ...styles.textarea,
              }}
            />
          </div>

          {/* PHOTO */}

          <div style={styles.group}>
            <label style={styles.label}>
              Photo
              <span style={styles.required}>
                {" "}*
              </span>
            </label>

            <div style={styles.uploadBox}>
              <input
                id="product-image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={loading}
                style={styles.fileInput}
                required
              />

              <p style={styles.help}>
                JPG, PNG, WEBP — maximum 5 Mo
              </p>
            </div>
          </div>

          {/* APERCU */}

          {preview && (
            <div style={styles.previewContainer}>
              <p style={styles.previewTitle}>
                Aperçu
              </p>

              <img
                src={preview}
                alt="Aperçu du produit"
                style={styles.preview}
              />
            </div>
          )}

          {/* BOUTON */}

          <button
            type="submit"
            disabled={
              loading ||
              loadingCategories
            }
            style={{
              ...styles.button,
              opacity:
                loading ||
                loadingCategories
                  ? 0.6
                  : 1,
              cursor:
                loading ||
                loadingCategories
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {loading
              ? "Ajout en cours..."
              : "Ajouter le produit"}
          </button>

          <p style={styles.requiredText}>
            * Champs obligatoires
          </p>
        </form>
      </div>
    </div>
  );
}

// =====================================================
// STYLE TEMPORAIRE
// =====================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f6f2",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "60px 20px",
    fontFamily:
      "Arial, Helvetica, sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "650px",
    background: "#ffffff",
    borderRadius: "14px",
    padding: "40px",
    boxShadow:
      "0 10px 35px rgba(0, 0, 0, 0.08)",
  },

  header: {
    marginBottom: "30px",
  },

  badge: {
    color: "#384525",
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    marginBottom: "8px",
  },

  title: {
    color: "#384525",
    fontSize: "30px",
    margin: "0 0 10px 0",
  },

  subtitle: {
    color: "#6b6b6b",
    fontSize: "15px",
    margin: 0,
  },

  group: {
    marginBottom: "22px",
  },

  label: {
    display: "block",
    color: "#333333",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
  },

  required: {
    color: "#b42318",
  },

  optional: {
    color: "#8a8a8a",
    fontSize: "12px",
    fontWeight: "400",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 14px",
    border: "1px solid #d8d8d8",
    borderRadius: "8px",
    background: "#ffffff",
    fontSize: "15px",
    outline: "none",
  },

  textarea: {
    resize: "vertical",
    minHeight: "110px",
    fontFamily:
      "Arial, Helvetica, sans-serif",
  },

  uploadBox: {
    padding: "18px",
    background: "#f8f9f6",
    border: "1px dashed #b9c0b3",
    borderRadius: "8px",
  },

  fileInput: {
    width: "100%",
  },

  help: {
    color: "#888888",
    fontSize: "12px",
    margin: "10px 0 0 0",
  },

  previewContainer: {
    marginBottom: "25px",
  },

  previewTitle: {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  },

  preview: {
    width: "180px",
    height: "180px",
    objectFit: "contain",
    background: "#f8f8f8",
    border: "1px solid #eeeeee",
    borderRadius: "8px",
    padding: "8px",
  },

  button: {
    width: "100%",
    border: "none",
    borderRadius: "8px",
    padding: "14px 20px",
    background: "#384525",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: "700",
  },

  success: {
    background: "#edf7ed",
    color: "#245c2b",
    border: "1px solid #c9e5cc",
    padding: "13px 15px",
    borderRadius: "8px",
    marginBottom: "22px",
    fontSize: "14px",
  },

  error: {
    background: "#fff1f0",
    color: "#b42318",
    border: "1px solid #f3c5c2",
    padding: "13px 15px",
    borderRadius: "8px",
    marginBottom: "22px",
    fontSize: "14px",
  },

  requiredText: {
    color: "#999999",
    fontSize: "11px",
    textAlign: "center",
    marginTop: "12px",
  },
};

export default AddProduct;